import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { 
  Upload, FileText, File, Check, AlertCircle, 
  Loader2, FlaskConical, X, CheckCircle, XCircle, Link2
} from "lucide-react";

export default function ImportPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parsedRecipe, setParsedRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [matchedIngredients, setMatchedIngredients] = useState([]);
  const [matching, setMatching] = useState(false);

  // Fetch inventory materials
  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const response = await axios.get(`${API}/materials`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMaterials(response.data);
      } catch (error) {
        console.error("Failed to fetch materials:", error);
      }
    };
    fetchMaterials();
  }, [token]);

  const allowedTypes = [
    "text/plain",
    "text/csv",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel"
  ];

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const isAllowed = allowedTypes.includes(selectedFile.type) || 
      selectedFile.name.endsWith('.txt') ||
      selectedFile.name.endsWith('.csv') ||
      selectedFile.name.endsWith('.pdf') ||
      selectedFile.name.endsWith('.xlsx') ||
      selectedFile.name.endsWith('.xls');

    if (!isAllowed) {
      toast.error("Unsupported file type. Use .txt, .csv, .pdf, or .xlsx");
      return;
    }

    setFile(selectedFile);
    setParsedRecipe(null);
    setMatchedIngredients([]);
    setError(null);
  };

  // Match ingredient name to inventory materials
  // ONLY matches existing inventory items - does NOT suggest replacements
  const findMatchingMaterial = (ingredientName) => {
    if (!ingredientName) return null;
    
    const normalizedName = ingredientName.toLowerCase().trim();
    
    // Try exact match first (highest confidence)
    let match = materials.find(m => 
      m.name.toLowerCase().trim() === normalizedName
    );
    
    if (match) return { material: match, matchType: 'exact' };
    
    // Try synonym matching using database synonyms field
    match = materials.find(m => {
      if (m.synonyms) {
        const synonyms = m.synonyms.toLowerCase().split(',').map(s => s.trim());
        // Only exact synonym matches, not partial
        return synonyms.some(synonym => synonym === normalizedName);
      }
      return false;
    });
    
    if (match) return { material: match, matchType: 'synonym' };
    
    // Try known fragrance name variations (bidirectional - ingredient name OR material name)
    const variations = {
      'bergamot': ['bergamote', 'bergamotte'],
      'bergamote': ['bergamot', 'bergamotte'],
      'iso e super': ['isoe super', 'iso-e-super', 'isoe'],
      'methyl ionone gamma': ['gamma methyl ionone', 'methyl gamma ionone'],
      'gamma methyl ionone': ['methyl ionone gamma', 'methyl gamma ionone'],
      'beta ionone': ['ionone beta', 'β ionone', 'β-ionone'],
      'ionone beta': ['beta ionone', 'β ionone', 'β-ionone'],
      'alpha ionone': ['ionone alpha', 'α ionone'],
      'ionone alpha': ['alpha ionone', 'α ionone'],
      'ethyl linalool': ['linalool ethyl'],
      'linalool ethyl': ['ethyl linalool'],
      'ethyl vanillin': ['vanillin ethyl'],
      'vanillin ethyl': ['ethyl vanillin'],
      'hedione hc': ['hedione', 'hc hedione'],
      'hedione': ['hedione hc'],
      'dihydro myrcenol': ['dihydromyrcenol', 'myrcenol dihydro'],
      'dihydromyrcenol': ['dihydro myrcenol'],
      'phenethyl alcohol': ['phenylethyl alcohol', '2-phenylethanol'],
      'phenylethyl alcohol': ['phenethyl alcohol'],
      'ambrox super': ['ambroxan', 'ambroxide', 'ambrox'],
      'ambroxan': ['ambrox super', 'ambrox'],
      'cashmeran': ['cashmere', 'cashmeran wood'],
      'coumarin': ['cumarin', 'coumarins'],
      'galaxolide': ['galaxolide 50', 'galaxolide 50%'],
      'galaxolide 50': ['galaxolide', 'galaxolide 50%'],
      'cis-3-hexenol': ['leaf alcohol', 'cis 3 hexenol', 'z-3-hexenol'],
      'leaf alcohol': ['cis-3-hexenol'],
      'raspberry ketone': ['frambinone', 'rheosmin'],
      'frambinone': ['raspberry ketone'],
      'delta damascone': ['damascone delta', 'δ-damascone'],
      'damascone delta': ['delta damascone']
    };
    
    // Check if ingredient name has a known variation
    const ingredientVariations = variations[normalizedName] || [];
    if (ingredientVariations.length > 0) {
      match = materials.find(m => {
        const matName = m.name.toLowerCase().trim();
        return matName === normalizedName || ingredientVariations.includes(matName);
      });
      if (match) return { material: match, matchType: 'variation' };
    }
    
    // Check if any material name matches ingredient via variation lookup
    match = materials.find(m => {
      const matName = m.name.toLowerCase().trim();
      const matVariations = variations[matName] || [];
      return matVariations.includes(normalizedName);
    });
    if (match) return { material: match, matchType: 'variation' };
    
    // Try matching by removing common suffixes (Oil, Synthetic, Natural, etc.)
    // But ONLY if the base names match exactly
    const stripSuffixes = (name) => {
      return name
        .replace(/\s+(oil|absolute|co2|extract|synthetic|natural|resinoid|abs|hc|firmenich|givaudan|iff|symrise)\s*$/gi, '')
        .replace(/\s+(10%|20%|50%|100%)\s*$/gi, '')
        .trim();
    };
    
    const cleanIngredientName = stripSuffixes(normalizedName);
    
    match = materials.find(m => {
      const cleanMatName = stripSuffixes(m.name.toLowerCase().trim());
      // Require exact match after stripping suffixes
      return cleanMatName === cleanIngredientName;
    });
    
    if (match) return { material: match, matchType: 'fuzzy' };
    
    // NO MATCH FOUND - return null (ingredient will show as unmatched)
    return null;
  };

  // Match all parsed ingredients to inventory using AI-powered synonym matching
  const matchIngredientsToInventory = async () => {
    if (!parsedRecipe?.ingredients) return;
    
    setMatching(true);
    
    try {
      // Call the AI-powered matching endpoint
      const response = await axios.post(
        `${API}/ai/match-ingredients-to-inventory`,
        parsedRecipe.ingredients,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.matched) {
        // Convert backend response to frontend format
        const matched = response.data.matched.map(m => {
          // Find the full material object from our materials list
          const material = m.material_id 
            ? materials.find(mat => mat.material_id === m.material_id) 
            : null;
          
          return {
            original_name: m.original_name,
            percentage: parseFloat(m.percentage) || 0,
            matched: m.matched,
            matchType: m.match_type,
            material: material,
            // Keep backend data as fallback if material not found locally
            material_id: m.material_id,
            material_name: m.material_name
          };
        });
        
        setMatchedIngredients(matched);
        toast.success(`AI matched ${response.data.match_count} of ${response.data.total_count} ingredients to inventory`);
      } else {
        // Fallback to local matching if AI fails
        fallbackLocalMatching();
      }
    } catch (error) {
      console.error("AI matching failed, using local fallback:", error);
      toast.warning("AI matching unavailable, using local matching");
      fallbackLocalMatching();
    } finally {
      setMatching(false);
    }
  };
  
  // Fallback local matching (without AI)
  const fallbackLocalMatching = () => {
    const matched = parsedRecipe.ingredients.map(ing => {
      const ingredientName = ing.material_name || ing.name || "";
      const result = findMatchingMaterial(ingredientName);
      
      return {
        original_name: ingredientName,
        percentage: parseFloat(ing.percentage) || 0,
        matched: result !== null,
        matchType: result?.matchType || null,
        material: result?.material || null
      };
    });
    
    setMatchedIngredients(matched);
    const matchedCount = matched.filter(m => m.matched).length;
    toast.success(`Matched ${matchedCount} of ${matched.length} ingredients to inventory`);
  };

  const handleParse = async () => {
    if (!file) return;

    setParsing(true);
    setError(null);
    setMatchedIngredients([]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/ai/parse-recipe-file`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.error) {
        setError(response.data.error);
        toast.error("Failed to parse recipe");
      } else {
        // Debug logging
        console.log("Parsed recipe from API:", response.data);
        if (response.data.ingredients) {
          const total = response.data.ingredients.reduce((sum, ing) => sum + (parseFloat(ing.percentage) || 0), 0);
          console.log("Total percentage from API:", total);
          console.log("First 5 ingredients:", response.data.ingredients.slice(0, 5));
        }
        setParsedRecipe(response.data);
        toast.success("Recipe parsed successfully!");
      }
    } catch (error) {
      setError(error.response?.data?.detail || "Failed to parse file");
      toast.error("Failed to parse recipe file");
    } finally {
      setParsing(false);
    }
  };

  // Auto-match after parsing
  useEffect(() => {
    if (parsedRecipe && materials.length > 0 && matchedIngredients.length === 0) {
      matchIngredientsToInventory();
    }
  }, [parsedRecipe, materials]);

  const handleSave = async () => {
    if (!parsedRecipe) return;

    setSaving(true);
    try {
      // Use matched ingredients with proper material IDs
      const ingredients = matchedIngredients.map(ing => {
        if (ing.matched && (ing.material || ing.material_id)) {
          // Use material object if available, otherwise use the IDs from backend
          const mat = ing.material;
          return {
            material_id: mat?.material_id || ing.material_id,
            material_name: mat?.name || ing.material_name || ing.original_name,
            percentage: ing.percentage,
            grams: null,
            duration: mat?.duration || "unclassified",
            solution_percentage: mat?.solution_percentage || 100,
            solution_type: mat?.solution_type || "Alcohol",
            price_per_gram: mat?.price_per_gram || 0
          };
        } else {
          // Unmatched ingredient - keep original name
          return {
            material_id: `unmatched_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            material_name: ing.original_name,
            percentage: ing.percentage,
            grams: null,
            duration: "unclassified"
          };
        }
      });

      const payload = {
        name: parsedRecipe.name || "Imported Recipe",
        description: parsedRecipe.description || "",
        total_grams: 100,
        concentration_percent: 20,
        ingredients,
        notes: parsedRecipe.notes || `Imported from ${file?.name || 'file'}`
      };

      const response = await axios.post(`${API}/recipes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Recipe saved successfully!");
      navigate(`/recipe-builder/${response.data.recipe_id}`);
    } catch (error) {
      toast.error("Failed to save recipe");
    } finally {
      setSaving(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setParsedRecipe(null);
    setMatchedIngredients([]);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileIcon = () => {
    if (!file) return <FileText className="w-6 h-6" />;
    if (file.name.endsWith('.pdf')) return <File className="w-6 h-6 text-red-400" />;
    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) return <File className="w-6 h-6 text-green-400" />;
    return <FileText className="w-6 h-6 text-blue-400" />;
  };

  const matchedCount = matchedIngredients.filter(m => m.matched).length;
  const unmatchedCount = matchedIngredients.filter(m => !m.matched).length;

  return (
    <div className="min-h-screen bg-noir flex">
      <Sidebar />
      
      <main className="flex-1 p-8 ml-64">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-playfair text-3xl font-bold text-white mb-2">
              Import Recipe
            </h1>
            <p className="text-zinc-400">Upload a file and AI will parse it and match ingredients to your inventory</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Upload Section */}
            <Card className="bg-noir-paper border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Upload File</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-none p-8 text-center cursor-pointer transition-colors ${
                    file 
                      ? "border-amber/50 bg-amber/5" 
                      : "border-zinc-700 hover:border-zinc-600"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.csv,.pdf,.xlsx,.xls"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  
                  {file ? (
                    <div className="space-y-3">
                      <div className="w-12 h-12 bg-amber/20 rounded-full flex items-center justify-center mx-auto text-amber">
                        {getFileIcon()}
                      </div>
                      <div>
                        <p className="text-white font-medium">{file.name}</p>
                        <p className="text-zinc-500 text-sm">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearFile();
                        }}
                        className="text-zinc-400 hover:text-white"
                      >
                        <X className="w-4 h-4 mr-1" /> Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Upload className="w-10 h-10 text-zinc-500 mx-auto" />
                      <div>
                        <p className="text-white">Click to upload</p>
                        <p className="text-zinc-500 text-sm">
                          Supports .txt, .csv, .pdf, .xlsx
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {file && !parsedRecipe && (
                  <Button
                    data-testid="parse-file-btn"
                    onClick={handleParse}
                    disabled={parsing}
                    className="w-full mt-4 bg-amber hover:bg-amber-light text-black"
                  >
                    {parsing ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Parsing with AI...
                      </span>
                    ) : (
                      "Parse Recipe"
                    )}
                  </Button>
                )}

                {error && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-400 mt-0.5" />
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}

                {/* Inventory Info */}
                <div className="mt-4 p-3 bg-zinc-800/50 border border-zinc-700">
                  <p className="text-zinc-400 text-sm flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    {materials.length} materials in your inventory for matching
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Parsed Result */}
            <Card className="bg-noir-paper border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span>Parsed Result</span>
                  {matchedIngredients.length > 0 && (
                    <span className="text-sm font-normal">
                      <span className="text-green-400">{matchedCount} matched</span>
                      {unmatchedCount > 0 && (
                        <span className="text-zinc-500"> • <span className="text-red-400">{unmatchedCount} not found</span></span>
                      )}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!parsedRecipe ? (
                  <div className="text-center py-12">
                    <FlaskConical className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
                    <p className="text-zinc-500">
                      {parsing ? "AI is parsing your file..." : "Upload and parse a file to see the result"}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Name</p>
                      <p className="text-white font-medium">{parsedRecipe.name || "Untitled Recipe"}</p>
                    </div>

                    {parsedRecipe.description && (
                      <div>
                        <p className="text-zinc-500 text-xs uppercase tracking-wider mb-1">Description</p>
                        <p className="text-zinc-300 text-sm">{parsedRecipe.description}</p>
                      </div>
                    )}

                    <div>
                      <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">
                        Ingredients ({matchedIngredients.length})
                      </p>
                      
                      {/* Percentage Summary */}
                      {matchedIngredients.length > 0 && (
                        <div className="mb-3 p-2 bg-zinc-800/50 border border-zinc-700 flex items-center justify-between">
                          <span className="text-zinc-400 text-sm">Total Percentage:</span>
                          <span className={`font-mono font-semibold ${
                            (() => {
                              const total = matchedIngredients.reduce((sum, ing) => sum + (ing.percentage || 0), 0);
                              if (total > 102) return 'text-red-400';
                              if (total < 98) return 'text-amber';
                              return 'text-green-400';
                            })()
                          }`}>
                            {matchedIngredients.reduce((sum, ing) => sum + (ing.percentage || 0), 0).toFixed(3)}%
                          </span>
                        </div>
                      )}
                      
                      <div className="max-h-[300px] overflow-y-auto space-y-1">
                        {matchedIngredients.map((ing, index) => (
                          <div 
                            key={index}
                            className={`flex items-center justify-between p-2 border ${
                              ing.matched 
                                ? "bg-green-500/5 border-green-500/20" 
                                : "bg-red-500/5 border-red-500/20"
                            }`}
                          >
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              {ing.matched ? (
                                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                              ) : (
                                <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                              )}
                              <div className="min-w-0">
                                <p className={`text-sm truncate ${ing.matched ? 'text-white' : 'text-zinc-400'}`}>
                                  {ing.matched ? (ing.material?.name || ing.material_name || ing.original_name) : ing.original_name}
                                </p>
                                {ing.matched && ing.matchType && (
                                  <p className="text-xs text-zinc-500 truncate">
                                    {ing.matchType === 'exact' ? '✓ Exact match' :
                                     ing.matchType === 'synonym' ? '↔ Synonym match' :
                                     ing.matchType === 'variation' ? '≈ Variation match' :
                                     ing.matchType === 'partial' ? '⊃ Partial match' : '≈ Fuzzy match'}
                                    {ing.original_name.toLowerCase() !== (ing.material?.name || ing.material_name || ing.original_name).toLowerCase() && (
                                      <span> ← {ing.original_name}</span>
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                            <span className={`font-mono text-sm flex-shrink-0 ml-2 ${ing.matched ? 'text-amber' : 'text-zinc-500'}`}>
                              {ing.percentage.toFixed(3)}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Percentage validation warning */}
                    {matchedIngredients.length > 0 && (() => {
                      const total = matchedIngredients.reduce((sum, ing) => sum + (ing.percentage || 0), 0);
                      if (total > 102) {
                        return (
                          <div className="p-3 bg-red-500/10 border border-red-500/30">
                            <p className="text-red-400 text-sm">
                              ⚠️ <strong>Total exceeds 100%</strong> ({total.toFixed(3)}%). 
                              This recipe may need normalization or there could be parsing errors.
                            </p>
                          </div>
                        );
                      } else if (total < 98) {
                        return (
                          <div className="p-3 bg-amber/10 border border-amber/30">
                            <p className="text-amber text-sm">
                              ⚠️ <strong>Total is less than 100%</strong> ({total.toFixed(3)}%). 
                              Some ingredients might be missing from the original recipe.
                            </p>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {unmatchedCount > 0 && (
                      <div className="p-3 bg-amber/10 border border-amber/30">
                        <p className="text-amber text-sm">
                          <strong>{unmatchedCount}</strong> ingredients not found in your inventory. 
                          They will be added as unlinked ingredients. You can add them to your inventory later.
                        </p>
                      </div>
                    )}

                    <Button
                      data-testid="save-parsed-recipe-btn"
                      onClick={handleSave}
                      disabled={saving || matching}
                      className="w-full bg-amber hover:bg-amber-light text-black"
                    >
                      {saving ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Save & Edit Recipe
                        </span>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Instructions */}
          <Card className="bg-noir-paper border-zinc-800 mt-6">
            <CardHeader>
              <CardTitle className="text-white">How it works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-amber/20 text-amber flex items-center justify-center flex-shrink-0 font-bold">1</div>
                  <div>
                    <p className="text-white font-medium">Upload Recipe File</p>
                    <p className="text-zinc-500 text-sm">Support for .txt, .csv, .xlsx, .pdf files</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-amber/20 text-amber flex items-center justify-center flex-shrink-0 font-bold">2</div>
                  <div>
                    <p className="text-white font-medium">AI Parses & Matches</p>
                    <p className="text-zinc-500 text-sm">Ingredients are automatically linked to your inventory</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-8 h-8 bg-amber/20 text-amber flex items-center justify-center flex-shrink-0 font-bold">3</div>
                  <div>
                    <p className="text-white font-medium">Save & Use</p>
                    <p className="text-zinc-500 text-sm">Recipe is ready with linked materials and stock tracking</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
